// Cliente API del webmail — Django /admin/webmail (proxy /django-api + Bearer).
// Mismo patrón que galan-inbox/api.ts.

import type {
  ComposeDraft, MailFolder, MailListResult, MailMessage,
} from './types';

const DJANGO_PROXY = '/django-api';
const BASE = '/api/v1/admin/webmail';

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token');
  return { Authorization: `Bearer ${token ?? ''}` };
}

async function asJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`${res.status} ${res.statusText} — ${body.slice(0, 200)}`);
  }
  return res.json() as Promise<T>;
}

export async function listFolders(): Promise<{ mailbox: string; folders: MailFolder[] }> {
  const res = await fetch(`${DJANGO_PROXY}${BASE}/folders`, { headers: authHeaders() });
  return asJson(res);
}

export async function listMessages(
  folder: string, page = 1, pageSize = 30, search = '', label = '',
): Promise<MailListResult> {
  const p = new URLSearchParams({ folder, page: String(page), page_size: String(pageSize) });
  if (search) p.set('search', search);
  if (label) p.set('label', label);
  const res = await fetch(`${DJANGO_PROXY}${BASE}/messages?${p.toString()}`, { headers: authHeaders() });
  return asJson(res);
}

export interface FolderStatus { folder: string; messages: number; uidnext: number; unseen: number }

/** Chequeo LIGERO (1 STATUS IMAP) para detectar cambios sin refrescar todo. */
export async function getStatus(folder: string): Promise<FolderStatus> {
  const res = await fetch(`${DJANGO_PROXY}${BASE}/status?folder=${encodeURIComponent(folder)}`, { headers: authHeaders() });
  return asJson(res);
}

export async function getMessage(
  folder: string, uid: string, markSeen = true,
): Promise<MailMessage> {
  const p = new URLSearchParams({ folder, uid, mark_seen: String(markSeen) });
  const res = await fetch(`${DJANGO_PROXY}${BASE}/message?${p.toString()}`, { headers: authHeaders() });
  return asJson(res);
}

export function attachmentUrl(folder: string, uid: string, idx: number): string {
  const token = localStorage.getItem('auth_token') ?? '';
  const p = new URLSearchParams({ folder, uid, idx: String(idx), token });
  return `${DJANGO_PROXY}${BASE}/attachment?${p.toString()}`;
}

export async function downloadAttachment(folder: string, uid: string, idx: number, name: string): Promise<void> {
  const p = new URLSearchParams({ folder, uid, idx: String(idx) });
  const res = await fetch(`${DJANGO_PROXY}${BASE}/attachment?${p.toString()}`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`No se pudo descargar (${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name || 'adjunto';
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export async function sendMail(draft: ComposeDraft): Promise<{ ok: boolean; message_id: string }> {
  const res = await fetch(`${DJANGO_PROXY}${BASE}/send`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(draft),
  });
  return asJson(res);
}

export async function saveDraft(draft: Omit<ComposeDraft, 'in_reply_to'>): Promise<{ ok: boolean }> {
  const res = await fetch(`${DJANGO_PROXY}${BASE}/draft`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(draft),
  });
  return asJson(res);
}

export async function setFlags(
  folder: string, uid: string, flags: { seen?: boolean; flagged?: boolean },
): Promise<{ ok: boolean }> {
  const res = await fetch(`${DJANGO_PROXY}${BASE}/flags`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ folder, uid, ...flags }),
  });
  return asJson(res);
}

/** Añade/quita una etiqueta (keyword IMAP) a un mensaje. Devuelve las etiquetas
 *  resultantes del mensaje. Sincroniza con todos los clientes IMAP del buzón. */
export async function setLabel(
  folder: string, uid: string, label: string, add: boolean,
): Promise<{ ok: boolean; labels: string[] }> {
  const res = await fetch(`${DJANGO_PROXY}${BASE}/label`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ folder, uid, label, add }),
  });
  return asJson(res);
}

export async function moveMessage(folder: string, uid: string, dest: string): Promise<{ ok: boolean }> {
  const res = await fetch(`${DJANGO_PROXY}${BASE}/move`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ folder, uid, dest }),
  });
  return asJson(res);
}

export async function deleteMessage(folder: string, uid: string): Promise<{ ok: boolean }> {
  const res = await fetch(`${DJANGO_PROXY}${BASE}/delete`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ folder, uid }),
  });
  return asJson(res);
}

export interface TriageInfo {
  importance: 'P1' | 'P2' | 'P3';
  summary: string;
  reason: string;
  draft: string;
  // Conversación / cliente / reenvío (sólo correos triados desde 2026-09-27).
  threadKey: string;
  threadCount: number;
  threadSummary: string;
  clientKey: string;
  clientName: string;
  forwardStatus: string;
  forwardedTo: string;
}

/** Overlay de TRIAGE IA para los correos VISIBLES del INBOX → mapa uid → triage.
 *  La IA ya clasificó/resumió/redactó en segundo plano (no-destructivo); aquí la
 *  pedimos sólo para la página que se está mostrando, por UID IMAP (con respaldo
 *  remitente+asunto para correos triados antes del 2026-09-27). Ligero: sin
 *  cuerpos ni adjuntos. Best-effort: si falla, la bandeja se ve igual sin IA. */
export async function fetchTriageFor(
  messages: Array<{ uid: string; from_addr: string; subject: string }>,
): Promise<Record<string, TriageInfo>> {
  if (!messages.length) return {};
  try {
    const res = await fetch(`${DJANGO_PROXY}/api/v1/admin/inbox/triage-lookup`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages }),
    });
    if (!res.ok) return {};
    const data = (await res.json()) as {
      items: Record<string, {
        importance: 'P1' | 'P2' | 'P3'; summary?: string; reason?: string; draft?: string;
        thread_key?: string; thread_count?: number; thread_summary?: string;
        client_key?: string; client_name?: string; forward_status?: string; forwarded_to?: string;
      }>;
    };
    const map: Record<string, TriageInfo> = {};
    for (const [uid, it] of Object.entries(data.items || {})) {
      map[uid] = {
        importance: it.importance, summary: it.summary || '', reason: it.reason || '', draft: it.draft || '',
        threadKey: it.thread_key || '', threadCount: it.thread_count || 0, threadSummary: it.thread_summary || '',
        clientKey: it.client_key || '', clientName: it.client_name || '',
        forwardStatus: it.forward_status || '', forwardedTo: it.forwarded_to || '',
      };
    }
    return map;
  } catch {
    return {};
  }
}

// ── Conversaciones y clientes (backend /admin/inbox, buzón propio) ─────────
const INBOX = '/api/v1/admin/inbox';

export interface ThreadMsg {
  item_id: number; uid: string; from_name: string; from_addr: string;
  subject: string; received_at: string | null; summary: string; importance: 'P1' | 'P2' | 'P3';
}
export interface ThreadInfo {
  thread_key: string; subject: string; message_count: number; last_at: string | null;
  summary: string; client_key: string; client_name: string; latest_uid?: string;
}
export interface ClientInfo {
  client_key: string; name: string; threads: number; messages: number; last_at: string | null;
}

export async function fetchThread(key: string): Promise<{ thread: ThreadInfo; messages: ThreadMsg[] }> {
  const res = await fetch(`${DJANGO_PROXY}${INBOX}/thread?key=${encodeURIComponent(key)}`, { headers: authHeaders() });
  return asJson(res);
}

export async function fetchClients(): Promise<ClientInfo[]> {
  const res = await fetch(`${DJANGO_PROXY}${INBOX}/clients`, { headers: authHeaders() });
  return asJson(res);
}

export async function fetchClientThreads(key: string): Promise<ThreadInfo[]> {
  const res = await fetch(`${DJANGO_PROXY}${INBOX}/clients/threads?key=${encodeURIComponent(key)}`, { headers: authHeaders() });
  return asJson(res);
}

export async function renameClient(clientKey: string, name: string): Promise<{ client_key: string; name: string }> {
  const res = await fetch(`${DJANGO_PROXY}${INBOX}/clients/name`, {
    method: 'PUT',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ client_key: clientKey, name }),
  });
  return asJson(res);
}
